import React, {createContext, useContext, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import useEffectEvent from '../hooks/use-effect-event'
import useNavigation from '../hooks/use-navigation'
import useHydration from './hydration'

/* Product configurator state context - allows to obtain the current status of product configurator */
const ProductConfiguratorStateContext = createContext()
const ProductConfiguratorStateProvider = ({children, isConfiguratorActive}) => {
    return (
        <ProductConfiguratorStateContext.Provider value={isConfiguratorActive}>
            {children}
        </ProductConfiguratorStateContext.Provider>
    )
}
ProductConfiguratorStateProvider.propTypes = {
    children: PropTypes.node,
    isConfiguratorActive: PropTypes.bool
}
/**
 * Obtain product configurator state
 * @returns {boolean} - true if the product configurator is active
 */
export const useProductConfiguratorState = () => {
    const ctx = useContext(ProductConfiguratorStateContext)
    if (ctx == null) {
        throw new Error(
            'useProductConfiguratorState must be used within a ProductConfiguratorStateProvider'
        )
    }
    return ctx
}

/* Product configurator control context - allows to activate/deactivate product configurator */
const ProductConfiguratorControlContext = createContext()
const ProductConfiguratorControlProvider = ({children, setConfiguratorActive}) => {
    return (
        <ProductConfiguratorControlContext.Provider value={setConfiguratorActive}>
            {children}
        </ProductConfiguratorControlContext.Provider>
    )
}
ProductConfiguratorControlProvider.propTypes = {
    children: PropTypes.node,
    setConfiguratorActive: PropTypes.func
}
/**
 * Obtain product configurator control
 * @returns {(boolean) => void} - function to activate/deactive the product configurator
 */
export const useProductConfiguratorControl = () => {
    const ctx = useContext(ProductConfiguratorControlContext)
    if (!ctx) {
        throw new Error(
            'useProductConfiguratorControl must be used within a ProductConfiguratorControlProvider'
        )
    }
    return ctx
}

export const ProductConfiguratorProvider = ({
    children,
    isConfiguratorActive,
    setConfiguratorActive
}) => {
    return (
        <ProductConfiguratorControlProvider setConfiguratorActive={setConfiguratorActive}>
            <ProductConfiguratorStateProvider isConfiguratorActive={isConfiguratorActive}>
                {children}
            </ProductConfiguratorStateProvider>
        </ProductConfiguratorControlProvider>
    )
}

ProductConfiguratorProvider.propTypes = {
    children: PropTypes.node,
    isConfiguratorActive: PropTypes.bool,
    setConfiguratorActive: PropTypes.func
}

/**
 * Provides product configurator state management and context provider
 * @returns {[boolean, React.ComponentType]} Tuple containing:
 *   - isConfiguratorActive: true if the product configurator is active
 *   - Provider: React component that provides configurator state and control to its children
 */
export const useProductConfigurator = () => {
    const location = useLocation()
    const navigate = useNavigation()
    const isHydrated = useHydration()

    // this is a quick workaround to hide header, newsletter and footer,
    // when showing product configurator
    // use the history state to not expose the parameter in the url, but still save it in the history
    // note: need to wait for hydration to complete or will render different html than ssr
    const isConfiguratorActive = isHydrated ? location?.state?.isConfiguratorActive || false : false
    const setConfiguratorActive = useEffectEvent((isActive) => {
        if (isActive) {
            // we want to have back navigation to the non-configurator state
            navigate(location.pathname + location.search, 'push', {
                ...location.state,
                isConfiguratorActive: true
            })
        } else {
            // first use replace action because it is sync and allows for quick update of the UI
            // then the next action will trigger goBack to discard this history state
            navigate(location.pathname + location.search, 'replace', {
                ...location.state,
                isConfiguratorActive: false
            })
        }
    })

    // go back the history stack if we are on a closed configurator state
    // this state only happens after closing configurator, and we know that the previous is
    // the pdp without configurator state
    useEffect(() => {
        if (location?.state?.isConfiguratorActive === false) {
            navigate('/', 'goBack')
        }
    }, [location?.state?.isConfiguratorActive, navigate])

    return {isConfiguratorActive, setConfiguratorActive}
}
